import Workflow, { ActionStatus, WorkflowAction, WorkflowStatus } from 'models/Workflow';

import { Tone } from './StatusBadge';

// The list's status vocabulary (the mockup's pills / legend). Distinct from the per-action
// badge labels — the list speaks in "Action Needed / Processing / Ready for Review / Complete".
export type ListStatus =
  | 'actionNeeded'
  | 'readyForReview'
  | 'processing'
  | 'complete'
  | 'error'
  | 'pending'
  | 'canceled';

// The quiet-pending Referral Outcome (OLIO-2611): the engine keeps it Pending so it never
// poisons cross-referral attention (list pills, notifications), but ON the referral page —
// the card badge and the rail, per the design — it presents as ready, because it is always
// recordable. Deliberately NOT folded into categoryStatus: the list rollups must stay quiet.
export function isQuietlyReady(action: WorkflowAction): boolean {
  return action.kind === 'AcceptReferral' && action.isPending;
}

// Every row has come to rest and at least one actually completed — skipped (not
// applicable) and canceled rows resting beside completed work read as Complete, not
// Pending. Shared by the card badge rollup and the rail's indicator (one predicate,
// two surfaces — they must never disagree).
export function completeWithParked(actions: WorkflowAction[]): boolean {
  return (
    actions.length > 0 &&
    actions.some((a) => a.isCompleted) &&
    actions.every((a) => a.isCompleted || a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  );
}

// A category's rolled-up status — the most significant state across its actions (same priority
// the category card header uses): an errored action wins (a failure must surface even when
// something else is ready for review), then blocked/flagged, then ready-for-review, then done,
// then in-flight.
export function categoryStatus(actions: WorkflowAction[]): ActionStatus {
  // Presentation priority reads effectiveStatus (a completed DenialCheck match / no-active-coverage
  // eligibility present as flagged); terminality (completeWithParked, canceled/skipped) reads the
  // real engine status — "has it come to rest" is a lifecycle fact, not a presentation one. Mirrors
  // rollupIndicator (StatusIcon.tsx) so the card badge and the rail icon never disagree. ERRORED
  // leads so a failed step is never masked by a flagged / ready-for-review one.
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ERRORED)) return ActionStatus.ERRORED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.FLAGGED || a.stale)) return ActionStatus.FLAGGED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ACTION_REQUIRED)) return ActionStatus.ACTION_REQUIRED;
  if (completeWithParked(actions)) return ActionStatus.COMPLETED;
  if (actions.some((a) => a.effectiveStatus === ActionStatus.RUNNING)) return ActionStatus.RUNNING;
  if (
    actions.length > 0 &&
    actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  )
    return ActionStatus.CANCELED;
  return ActionStatus.PENDING;
}

const STATUS_TO_LIST: Record<ActionStatus, ListStatus> = {
  [ActionStatus.FLAGGED]: 'actionNeeded',
  [ActionStatus.ACTION_REQUIRED]: 'readyForReview',
  [ActionStatus.ERRORED]: 'error',
  [ActionStatus.RUNNING]: 'processing',
  [ActionStatus.COMPLETED]: 'complete',
  [ActionStatus.PENDING]: 'pending',
  [ActionStatus.CANCELED]: 'pending',
  [ActionStatus.SKIPPED]: 'pending',
};

// `label` is the per-row status pill; `legendLabel` (when present) is the legend swatch caption.
// They diverge for `processing`: the pill reads "Processing" (with the AI sparkle) while the
// legend names the state "Olio Agent".
export const LIST_STATUS_DISPLAY: Record<ListStatus, { label: string; legendLabel?: string; tone: Tone }> = {
  actionNeeded: { label: 'Action Needed', tone: 'warning' },
  readyForReview: { label: 'Ready for Review', tone: 'review' },
  processing: { label: 'Processing', legendLabel: 'Olio Agent', tone: 'running' },
  complete: { label: 'Complete', tone: 'success' },
  error: { label: 'Error', tone: 'danger' },
  pending: { label: 'Pending', tone: 'neutral' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

// Most-urgent first — drives the overall pill (the most significant category wins). Error leads
// so a failed step is never masked by a ready-for-review one (matches categoryStatus's order).
const PRIORITY: ListStatus[] = ['error', 'actionNeeded', 'readyForReview', 'processing', 'pending', 'complete'];

// One segment per category, colored by that category's status.
export function categorySegments(workflow: Workflow): { key: string; name: string; status: ListStatus }[] {
  return workflow.actionsByCategory().map(({ category, actions }) => ({
    key: category.key,
    name: category.name,
    status: STATUS_TO_LIST[categoryStatus(actions)],
  }));
}

// The list pill: the highest-priority category status (or Complete when the run is done).
// A canceled run (e.g. confirmed duplicate, OLIO-2729) says so — the all-canceled category
// rollup would otherwise read as Pending, indistinguishable from not-started.
export function overallListStatus(workflow: Workflow): ListStatus {
  if (workflow.status === WorkflowStatus.CANCELED) return 'canceled';
  if (workflow.isCompleted) return 'complete';
  const present = new Set(categorySegments(workflow).map((segment) => segment.status));
  return PRIORITY.find((status) => present.has(status)) ?? 'processing';
}

// "X of N – <current category>" — current is the first not-yet-complete category (the last
// once everything's done).
export function progressSummary(workflow: Workflow): { index: number; total: number; categoryName: string } {
  const categories = workflow.actionsByCategory();
  const total = categories.length;
  const currentIndex = categories.findIndex(({ actions }) => categoryStatus(actions) !== ActionStatus.COMPLETED);
  const resolvedIndex = currentIndex === -1 ? total - 1 : currentIndex;
  return {
    index: resolvedIndex + 1,
    total,
    categoryName: categories[resolvedIndex]?.category.name ?? '',
  };
}
